import React, { useEffect } from "react"
import { convertMinutesTo12HHMM } from "../../utils/generalUtils"
import { getSeverityColor } from "./ExpandedView/FuturePatientList"

const PatientInfoDialog = ({ isOpen, onClose, patient, issues = [] }) => {
	// Handle escape key press
	useEffect(() => {
		const handleEscapeKey = event => {
			if (event.key === "Escape") {
				onClose()
			}
		}

		// Add event listener when modal is open
		if (isOpen) {
			document.addEventListener("keydown", handleEscapeKey)
		}

		// Cleanup function to remove event listener
		return () => {
			document.removeEventListener("keydown", handleEscapeKey)
		}
	}, [isOpen, onClose])

	if (!isOpen) return null

	return (
		<div className="fixed inset-0 z-[110] overflow-y-auto backdrop-blur-sm">
			<div className="flex items-center justify-center min-h-screen p-4">
				<div
					className="fixed inset-0 bg-black bg-opacity-40 transition-opacity"
					onClick={onClose}
				></div>

				<div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-0 relative z-10 overflow-hidden">
					{/* Header with solid background */}
					<div className="bg-customBlue p-6 text-white">
						<div className="flex justify-between items-center">
							<h2 className="text-2xl font-bold">Patient Information</h2>
							<button
								onClick={onClose}
								className="text-white hover:text-blue-100 focus:outline-none transition-colors"
							>
								<svg
									className="h-6 w-6"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>
					</div>

					<div className="p-6">
						{/* Patient info card */}
						<div className="rounded-xl p-5 mb-6 shadow-inner">
							<div className="flex items-center mb-4">
								<div className="bg-customBlue rounded-full p-3 mr-4">
									<svg
										className="h-8 w-8 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
										/>
									</svg>
								</div>
								<div>
									<p className="text-gray-500 text-sm">Patient Name</p>
									<p className="sensitive text-xl font-semibold text-gray-800">
										{patient.patientName}
									</p>
								</div>
								<div className="ml-auto">
									<p className="text-gray-500 text-sm">MRN</p>
									<p className="sensitive font-mono text-lg">
										{patient.patientMRN || "N/A"}
									</p>
								</div>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div className="bg-white rounded-lg p-3 shadow-sm">
									<p className="text-gray-500 text-sm">Treatment</p>
									<p className="font-medium">{patient.infusionType || "N/A"}</p>
								</div>
								<div className="bg-white rounded-lg p-3 shadow-sm">
									<p className="text-gray-500 text-sm">Acuity</p>
									<span
										className={`inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ring-1 ring-inset ${(() => {
											switch (patient.acuity) {
												case 0:
													return "bg-green-100 text-green-700 ring-green-300/20"
												case 1:
													return "bg-green-200 text-green-800 ring-green-400/20"
												case 2:
													return "bg-green-300 text-green-900 ring-green-500/20"
												case 3:
													return "bg-yellow-200 text-yellow-800 ring-yellow-400/20"
												case 4:
													return "bg-orange-200 text-orange-800 ring-orange-400/20"
												case 5:
													return "bg-red-200 text-red-800 ring-red-500/20"
												default:
													return "bg-gray-100 text-gray-700 ring-gray-400/20"
											}
										})()}`}
									>
										{patient.acuity}
									</span>
								</div>
							</div>
						</div>

						{/* Schedule information section */}
						<div className="mb-6">
							<h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
								<svg
									className="h-5 w-5 text-customBlue mr-2"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								Appointment Details
							</h3>
							<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
								<div className="grid grid-cols-2 divide-x divide-y divide-gray-200">
									<div className="p-4 hover:bg-gray-50 transition-colors">
										<p className="text-gray-500 text-sm">Scheduled Time</p>
										<p className="font-medium text-gray-800">
											{convertMinutesTo12HHMM(patient.scheduledStartTime)} -{" "}
											{convertMinutesTo12HHMM(patient.scheduledEndTime)}
										</p>
									</div>
									<div className="p-4 hover:bg-gray-50 transition-colors">
										<p className="text-gray-500 text-sm">Clinic Appointment</p>
										<p className="font-medium text-gray-800">
											{patient.clinicAppointmentTime
												? convertMinutesTo12HHMM(
														patient.clinicAppointmentTime
													)
												: "N/A"}
										</p>
									</div>
									<div className="p-4 hover:bg-gray-50 transition-colors">
										<p className="text-gray-500 text-sm">Reason for Visit</p>
										<p className="font-medium text-gray-800">
											{patient.reasonForVisit || "N/A"}
										</p>
									</div>
									<div className="p-4 hover:bg-gray-50 transition-colors">
										<p className="text-gray-500 text-sm">
											Referring Department
										</p>
										<p className="font-medium text-gray-800">
											{patient.department || "N/A"}
										</p>
									</div>
									<div className="p-4 col-span-2 hover:bg-gray-50 transition-colors">
										<p className="text-gray-500 text-sm">
											Clinic Appointment Type
										</p>
										<p className="font-medium text-gray-800">
											{patient.linkedClinicAppointment || "N/A"}
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Scheduling issues section */}
						<div className="mb-6">
							<h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
								<svg
									className="h-5 w-5 text-customBlue mr-2"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									/>
								</svg>
								Scheduling Issues
							</h3>

							{issues.length > 0 ? (
								<div className="space-y-3">
									{issues.map((issue, index) => (
										<div
											key={index}
											className={`p-4 rounded-lg shadow-sm border-l-4 ${(() => {
												switch (issue.severity) {
													case "error":
														return (
															"border-red-500 " +
															getSeverityColor(issue.severity)
														)
													case "warning":
														return (
															"border-yellow-500 " +
															getSeverityColor(issue.severity)
														)
													case "info":
														return (
															"border-blue-500 " +
															getSeverityColor(issue.severity)
														)
													default:
														return (
															"border-gray-500 " +
															getSeverityColor(issue.severity)
														)
												}
											})()}`}
										>
											<div className="font-medium flex items-center">
												{(() => {
													switch (issue.severity) {
														case "error":
															return (
																<svg
																	className="h-5 w-5 text-red-600 mr-2"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		strokeWidth={2}
																		d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
																	/>
																</svg>
															)
														case "warning":
															return (
																<svg
																	className="h-5 w-5 text-yellow-600 mr-2"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		strokeWidth={2}
																		d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
																	/>
																</svg>
															)
														case "info":
															return (
																<svg
																	className="h-5 w-5 text-blue-600 mr-2"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		strokeWidth={2}
																		d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
																	/>
																</svg>
															)
														default:
															return (
																<svg
																	className="h-5 w-5 text-gray-600 mr-2"
																	fill="none"
																	viewBox="0 0 24 24"
																	stroke="currentColor"
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		strokeWidth={2}
																		d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
																	/>
																</svg>
															)
													}
												})()}
												{issue.type || "Issue"}
											</div>
											<div className="text-sm mt-1 ml-7">
												{issue.message || "No details available"}
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="bg-gray-50 rounded-lg p-5 text-center">
									<svg
										className="h-12 w-12 text-gray-400 mx-auto mb-2"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									<p className="text-gray-500">No scheduling issues found</p>
								</div>
							)}
						</div>

						<div className="flex justify-end">
							<button
								onClick={onClose}
								className="px-5 py-2.5 bg-customBlue text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-300"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default PatientInfoDialog
